import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { Button, Dropdown, Menu, Popconfirm } from 'antd';
import {
	EditOutlined,
	DeleteOutlined,
	DownOutlined,
	ToolOutlined,
	DollarOutlined,
	ProfileOutlined,
} from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { OWStatCard as StatCard } from '../ow_stat_card/OWStatCard';
import { DELIVERABLE_STATUS_OPTIONS } from '../../constants/DeliverableStatuses';
import DeliverableStatusDisplay from '../deliverable_status_display/DeliverableStatusDisplay';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import DeliverableGallery from '../deliverable_gallery/DeliverableGallery';
import DeliverableDocuments from '../deliverable_documents/DeliverableDocuments';
import DeliverableWorkOrders from '../deliverable_work_orders/DeliverableWorkOrders';
import DeliverableTasks from '../deliverable_tasks/DeliverableTasks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	WORK_ORDER_METRIC_ROWS,
	budgetPercentUsed,
	formatCurrency,
	workOrderMetricsTotal,
} from '../../utils/DeliverableUtils';

// Right detail pane. Reads the server-hydrated deliverable directly — status,
// workOrderMetrics, taskMetrics, budgetUsed, assignedTo, gallery.
const DeliverableDetailPane = ({ deliverable, currency, onEdit, onDelete, onPatch }) => {
	const [activeTab, setActiveTab] = useState('overview');
	const fmt = (amount) => (currency ? currency.symbolFormat(amount, 0) : formatCurrency(amount));

	const status = nullSafeGet('status', deliverable);
	const workOrderMetrics = nullSafeGet('workOrderMetrics', deliverable);
	const woTotal = workOrderMetricsTotal(workOrderMetrics);
	const taskMetrics = nullSafeGet('taskMetrics', deliverable);
	const budget = nullSafeGet('budget', deliverable);
	const budgetUsed = nullSafeGet('budgetUsed', deliverable);
	const hasBudget = budget != null;
	const budgetPct = budgetPercentUsed(budget, budgetUsed);
	const dueDate = nullSafeGet('dueDate', deliverable);
	const assignedTo = nullSafeGetOrElse('assignedTo', deliverable, []);
	const gallery = nullSafeGetOrElse('gallery', deliverable, []);
	const workOrderCount = nullSafeGetOrElse('workOrders', deliverable, []).length;

	const detailTabs = [
		{ key: 'overview', label: 'Overview' },
		{ key: 'tasks', label: 'Tasks' },
		{ key: 'workOrders', label: `Work Orders (${workOrderCount})` },
	];

	return (
		<div className="flex flex-col p-4">
			<div className="mb-6 flex items-start justify-between gap-4">
				<div className="h4medium m-0 text-character-primary">
					{nullSafeGet('name', deliverable)}
				</div>
				<div className="flex shrink-0 items-center gap-2">
					<OWButton type="ghost" icon={<EditOutlined />} onClick={onEdit} />
					<Popconfirm
						title="Delete this deliverable?"
						okText="Delete"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={onDelete}
					>
						<OWButton type="ghost" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</div>
			</div>

			<div className="mb-4 flex flex-wrap gap-8">
				<div>
					<div className="bodyregular mb-1 text-character-secondary">Status</div>
					<div className="flex items-center gap-1">
						<DeliverableStatusDisplay status={status} />
						<Dropdown
							trigger={['click']}
							overlay={
								<Menu
									selectedKeys={status ? [status] : []}
									onClick={({ key }) => onPatch && onPatch({ status: key })}
									items={DELIVERABLE_STATUS_OPTIONS.map((option) => ({
										key: option,
										label: <DeliverableStatusDisplay status={option} />,
									}))}
								/>
							}
						>
							<Button
								type="text"
								size="small"
								className="flex items-center justify-center px-1 text-character-secondary"
								icon={<DownOutlined className="text-[10px]" />}
							/>
						</Dropdown>
					</div>
				</div>
				<div>
					<div className="bodyregular mb-1 text-character-secondary">Due On</div>
					<div className="bodyregular text-character-primary">
						{dueDate ? moment(dueDate).format('MMM D, YYYY') : '—'}
					</div>
				</div>
				<div>
					<div className="bodyregular mb-1 text-character-secondary">Assigned to</div>
					<div className="flex flex-wrap items-center gap-3">
						{assignedTo.length ? (
							assignedTo.map((email) => (
								<UserNameWithEmail
									key={email}
									email={email}
									mode="inline"
									showNameWithAvatar
									size="small"
								/>
							))
						) : (
							<span className="bodyregular text-character-disabled">Unassigned</span>
						)}
					</div>
				</div>
			</div>

			<div className="mb-6 flex gap-3">
				{/* Work Orders — total + status breakdown (Figma 12486:84757) */}
				<StatCard className="flex items-center gap-3">
					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-1">
							<ToolOutlined className="text-[12px] text-character-secondary" />
							<span className="bodyregular text-character-secondary">Work Orders</span>
						</div>
						<span className="h4medium text-character-primary">
							{workOrderMetrics ? woTotal : '—'}
						</span>
					</div>
					{workOrderMetrics ? (
						<div className="flex flex-1 flex-col border-0 border-l border-solid border-grey-5 pl-3">
							{WORK_ORDER_METRIC_ROWS.map((row) => (
								<div key={row.key} className="flex items-start gap-1">
									<span className="footnoteregular flex-1 text-character-secondary">
										{row.label}
									</span>
									<span className="footnoteMedium text-character-primary">
										{nullSafeGetOrElse(row.key, workOrderMetrics, 0)}
									</span>
								</div>
							))}
						</div>
					) : null}
				</StatCard>

				{/* Budget — used / total (Figma 12486:84781). Hidden when the deliverable
				    has no budget (project isn't on individual budget). */}
				{hasBudget ? (
					<StatCard className="flex flex-col justify-center gap-2">
						<div className="flex items-center gap-1">
							<DollarOutlined className="text-[12px] text-character-secondary" />
							<span className="bodyregular text-character-secondary">Budget</span>
							<span className="bodyregular text-character-secondary">·</span>
							<span className="footnoteMedium text-character-primary">{budgetPct}% Used</span>
						</div>
						<span className="h4medium text-character-primary">
							{`${fmt(budgetUsed != null ? budgetUsed : 0)} / ${fmt(budget)}`}
						</span>
					</StatCard>
				) : null}

				{/* Tasks — completed / total (Figma 12486:84795) */}
				<StatCard className="flex flex-col justify-center gap-2">
					<div className="flex items-center gap-1">
						<ProfileOutlined className="text-[12px] text-character-secondary" />
						<span className="bodyregular text-character-secondary">Tasks</span>
					</div>
					<span className="h4medium text-character-primary">
						{taskMetrics
							? `${nullSafeGetOrElse('tasksCompleted', taskMetrics, 0)}/${nullSafeGetOrElse(
									'tasksTotal',
									taskMetrics,
									0
							  )}`
							: '—'}
					</span>
				</StatCard>
			</div>

			<OWSubTabs tabs={detailTabs} activeKey={activeTab} onChange={setActiveTab} className="mb-4" />
			{activeTab === 'overview' ? (
				<>
					<div className="bodyregular mb-6 whitespace-pre-line text-character-primary">
						{nullSafeGetOrElse('description', deliverable, '')}
					</div>
					<div>
						<DeliverableDocuments deliverable={deliverable} />
						<div className="h5medium mb-2 mt-6 text-character-primary">
							Gallery ({gallery.length})
						</div>
						<DeliverableGallery gallery={gallery} />
					</div>
				</>
			) : null}
			{activeTab === 'tasks' ? (
				<DeliverableTasks
					projectId={nullSafeGet('projectId', deliverable)}
					deliverableId={nullSafeGet('id', deliverable)}
				/>
			) : null}
			{activeTab === 'workOrders' ? <DeliverableWorkOrders deliverable={deliverable} /> : null}
		</div>
	);
};

export default DeliverableDetailPane;
